/**
 * Post-generation invariant checks (dev builds and tests).
 */
import type { Track } from "../shared/types.ts";
import { segPoint } from "./geom.ts";

export function validateTrack(track: Track): string[] {
  const errors: string[] = [];
  const segs = track.loop.segments;
  if (segs.length === 0) return ["empty loop"];

  // 1. Centerline continuity around the closed loop (C0, ε for the small
  // floor-height mismatches between piece families, e.g. tunnel 0.20 vs 0.25).
  for (let i = 0; i < segs.length; i++) {
    const cur = segs[i].curve;
    const nxt = segs[(i + 1) % segs.length].curve;
    const a = segPoint(cur[cur.length - 1], 1);
    const b = segPoint(nxt[0], 0);
    const horiz = Math.hypot(a[0] - b[0], a[2] - b[2]);
    const vert = Math.abs(a[1] - b[1]);
    if (horiz > 1e-6 || vert > 0.06) {
      errors.push(
        `loop gap after segment ${i}: horiz ${horiz.toExponential(2)}, vert ${
          vert.toFixed(3)
        }`,
      );
    }
  }

  // 2. Elevation closure: net rise around the loop is zero (implied by 1's
  // vertical check chaining, but assert the sum explicitly for clarity).
  let rise = 0;
  for (const seg of segs) {
    const a = segPoint(seg.curve[0], 0);
    const b = segPoint(seg.curve[seg.curve.length - 1], 1);
    rise += b[1] - a[1];
  }
  if (Math.abs(rise) > 0.06 * segs.length) {
    errors.push(`elevation does not close: net rise ${rise}`);
  }

  // 3. Track placements do not share cells (footprints already enforced by
  // realization; assert supports/decor don't sit inside track cells).
  const trackPos = new Set(
    track.placements.filter((p) => p.role === "track").map((p) =>
      `${Math.round(p.pos[0] * 2)},${Math.round(p.pos[2] * 2)}`
    ),
  );
  for (const p of track.placements) {
    if (p.role !== "decor") continue;
    const k = `${Math.round(p.pos[0] * 2)},${Math.round(p.pos[2] * 2)}`;
    if (trackPos.has(k)) {
      errors.push(
        `decor ${p.pieceId} at ${p.pos} overlaps a track piece origin`,
      );
    }
  }

  // 4. Loop length sanity.
  if (!(track.loop.totalLength > 10)) {
    errors.push(`suspiciously short loop: ${track.loop.totalLength}`);
  }

  return errors;
}
